import React, { useEffect, useState, useMemo } from "react";
import { Box, Grid, Typography, CircularProgress, Alert, alpha } from "@mui/material";
import BarChartIcon from "@mui/icons-material/BarChart";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import useFetchWithMsal from "../../hooks/useFetchWithMsal";
import { protectedResources } from "../../authConfig";
import NotFoundPage from "../ErrorPage";
import TrendAreaChart from "../../components/StatisticsDashboard/TrendAreaChart";
import { CATEGORICAL_COLORS, getDefaultMonthRange } from "../../components/StatisticsDashboard/chartUtils";
import MonthRangeFilter from "../../components/StatisticsDashboard/MonthRangeFilter";
import StatusBreakdownChart from "../../components/StatisticsDashboard/StatusBreakdownChart";
import SavingsChart from "../../components/StatisticsDashboard/SavingsChart";
import DashboardCard from "../../components/StatisticsDashboard/DashboardCard";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import SavingsIcon from "@mui/icons-material/Savings";
import FactCheckIcon from "@mui/icons-material/FactCheck";

const BASE_URL = (process.env.REACT_APP_BACKEND_BASE_URL || "").trim();
const PAGE_COLOR = "#7c3aed";
// Same green used for the "savings" card on the other dashboards
// (CATEGORICAL_COLORS[2]) — keeps the "time saved" concept visually
// consistent across all of them, instead of the page's own purple.
const SAVINGS_CHART_COLOR = CATEGORICAL_COLORS[2];
const { fromMonthInput: DEFAULT_FROM, toMonthInput: DEFAULT_TO } = getDefaultMonthRange();

const FORM_CODE_GROUPS = [
  { key: 'ANT', label: 'ANT', color: CATEGORICAL_COLORS[0], icon: FiberManualRecordIcon },
  { key: 'BIO', label: 'BIO', color: CATEGORICAL_COLORS[1], icon: FiberManualRecordIcon },
  { key: 'PCA', label: 'PCA', color: CATEGORICAL_COLORS[2], icon: FiberManualRecordIcon },
  { key: 'TPN', label: 'TPN', color: CATEGORICAL_COLORS[3], icon: FiberManualRecordIcon },
  { key: 'TOX', label: 'TOX', color: CATEGORICAL_COLORS[6], icon: FiberManualRecordIcon },
  { key: 'FU', label: 'FU', color: CATEGORICAL_COLORS[5], icon: FiberManualRecordIcon },
  { key: 'MAG', label: 'MAG', color: CATEGORICAL_COLORS[4], icon: FiberManualRecordIcon },
  { key: 'GRAVPCA', label: 'GRAVPCA', color: CATEGORICAL_COLORS[7], icon: FiberManualRecordIcon },
  { key: 'SYRIPCA', label: 'SYRIPCA', color: '#795548', icon: FiberManualRecordIcon },
];


// Entry to /CleanRoomsDashboard only requires the "ManagersDashboardPage" role
// permission (checked by ProtectedRoute). Whether this specific sub-dashboard
// is enabled is a separate, narrower grant living in managerDashboardPermissionModel
// — so it's checked here, locally, against /api/managerDashboard/myAccess.
const CLEAN_ROOMS_DASHBOARD_KEY = "cleanRooms";

const CleanRoomsDashboardPage = () => {
  // "checking" | "granted" | "denied"
  const [dashboardAccess, setDashboardAccess] = useState("checking");
  const [monthlyFormsStats, setMonthlyFormsStats] = useState([]);
  const [loadingMonthlyFormsStats, setLoadingMonthlyFormsStats] = useState(true);
  const [minutesSavedPerForm, setMinutesSavedPerForm] = useState(15);
  const [minutesInput, setMinutesInput] = useState('15');
  const [savingMinutes, setSavingMinutes] = useState(false);
  const [error, setError] = useState(null);
  const [fromMonth, setFromMonth] = useState(DEFAULT_FROM);
  const [toMonth, setToMonth] = useState(DEFAULT_TO);

  const { execute: fetchRead } = useFetchWithMsal({ scopes: protectedResources.apiScopes.scopes.read });
  const { execute: fetchWrite } = useFetchWithMsal({ scopes: protectedResources.apiScopes.scopes.write });

  // cleanRoomsDashboardService (queries Priority) expects a plain YYYY-MM-DD
  // date and appends its own time suffix — not a full ISO datetime.
  const dateRange = useMemo(() => {
    if (!fromMonth || !toMonth) return null;
    const [fromYear, fromMonthNum] = fromMonth.split("-").map(Number);
    const [toYear, toMonthNum] = toMonth.split("-").map(Number);
    const pad = (n) => String(n).padStart(2, "0");
    const startDate = `${fromYear}-${pad(fromMonthNum)}-01`;
    const lastDay = new Date(toYear, toMonthNum, 0).getDate();
    const endDate = `${toYear}-${pad(toMonthNum)}-${pad(lastDay)}`;
    return { startDate, endDate };
  }, [fromMonth, toMonth]);

  useEffect(() => {
    let cancelled = false;

    // A definitive 403 means the backend evaluated access and said no.
    // Anything else (network hiccup, cold-start 5xx, token refresh blip) is a
    // failed check, not a denial — retry once before giving up.
    const checkAccess = async (isRetry = false) => {
      try {
        const result = await fetchRead("GET", `${BASE_URL}/api/managerDashboard/myAccess`);
        if (cancelled) return;
        const allowed = !!result?.isAdmin || !!result?.allowedDashboards?.includes(CLEAN_ROOMS_DASHBOARD_KEY);
        setDashboardAccess(allowed ? "granted" : "denied");
      } catch (err) {
        if (cancelled) return;
        if (err?.status === 403 || isRetry) {
          setDashboardAccess("denied");
          return;
        }
        setTimeout(() => checkAccess(true), 500);
      }
    };

    checkAccess();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!dateRange) return;
    let cancelled = false;
    const loadMonthlyFormsStats = async () => {
      setLoadingMonthlyFormsStats(true);
      setError(null);
      try {
        const params = new URLSearchParams(dateRange).toString();
        const res = await fetchRead("GET", `${BASE_URL}/api/cleanRoomsDashboard/monthly-stats?${params}`);
        if (cancelled) return;
        setMonthlyFormsStats(res.monthlyStats || []);
      } catch (err) {
        if (cancelled) return;
        setError(err?.msg || "שגיאה בטעינת מגמת שמירת הטפסים");
      } finally {
        if (!cancelled) setLoadingMonthlyFormsStats(false);
      }
    };
    loadMonthlyFormsStats();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange]);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const res = await fetchRead("GET", `${BASE_URL}/api/cleanRoomsDashboard/settings`);
        const value = res.settings?.minutesSavedPerForm ?? 15;
        setMinutesSavedPerForm(value);
        setMinutesInput(String(value));
      } catch (err) {
        console.error("Error loading clean-rooms-dashboard stats settings:", err);
      }
    };
    loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSaveMinutes = async () => {
    const value = Number(minutesInput);
    if (Number.isNaN(value) || value < 0) return;
    setSavingMinutes(true);
    try {
      const res = await fetchWrite("PUT", `${BASE_URL}/api/cleanRoomsDashboard/settings`, { minutesSavedPerForm: value });
      setMinutesSavedPerForm(res.settings?.minutesSavedPerForm ?? value);
    } catch (err) {
      console.error("Error saving clean-rooms-dashboard stats settings:", err);
    } finally {
      setSavingMinutes(false);
    }
  };

  if (dashboardAccess === "checking") {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress sx={{ color: PAGE_COLOR }} />
      </Box>
    );
  }

  if (dashboardAccess === "denied") {
    return <NotFoundPage />;
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, display: "flex", flexDirection: "column", gap: 3 }}>
      <Box display="flex" alignItems="center" gap={2}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "50%",
            bgcolor: alpha(PAGE_COLOR, 0.12),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <BarChartIcon sx={{ color: PAGE_COLOR, fontSize: 26 }} />
        </Box>
        <Box>
          <Typography variant="h5" fontWeight={700}>חדרים נקיים</Typography>
          <Typography variant="body2" color="text.secondary">
            תמונת מצב לפי טווח התאריכים שנבחר — משתנה בכל הדשבורדים למטה
          </Typography>
        </Box>
      </Box>

      <MonthRangeFilter
        fromMonth={fromMonth}
        toMonth={toMonth}
        onFromMonthChange={setFromMonth}
        onToMonthChange={setToMonth}
        color={PAGE_COLOR}
      />

      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <DashboardCard title="מגמת שמירת טפסים" icon={TrendingUpIcon} color={PAGE_COLOR}>
            {(expanded) => (
              loadingMonthlyFormsStats ? (
                <Box display="flex" justifyContent="center" p={4}><CircularProgress sx={{ color: PAGE_COLOR }} /></Box>
              ) : (
                <TrendAreaChart
                  monthlyData={monthlyFormsStats}
                  color={PAGE_COLOR}
                  expanded={expanded}
                  totalLabel="סה״כ טפסים שנשמרו בטווח שנבחר"
                  unitLabel="טפסים"
                  gradientId="cleanRoomsTrendGradient"
                />
              )
            )}
          </DashboardCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <DashboardCard title="חיסכון בזמן עבודה" subtitle="לפי כמות הטפסים שנשמרו בחודש" icon={SavingsIcon} color={SAVINGS_CHART_COLOR}>
            {(expanded) => (
              <SavingsChart
                monthlyData={monthlyFormsStats}
                color={SAVINGS_CHART_COLOR}
                minutesSavedPerUnit={minutesSavedPerForm}
                minutesInput={minutesInput}
                onMinutesInputChange={setMinutesInput}
                onSaveMinutes={handleSaveMinutes}
                saving={savingMinutes}
                expanded={expanded}
                minutesFieldLabel="דקות שנחסכות לטופס"
              />
            )}
          </DashboardCard>
        </Grid>
      </Grid>

      <Box>
        <DashboardCard title="פילוח טפסים לפי סוג טופס" icon={FactCheckIcon} color={CATEGORICAL_COLORS[0]}>
          {() => (
            loadingMonthlyFormsStats ? (
              <Box display="flex" justifyContent="center" p={4}><CircularProgress sx={{ color: PAGE_COLOR }} /></Box>
            ) : (
              <StatusBreakdownChart
                monthlyData={monthlyFormsStats}
                statusGroups={FORM_CODE_GROUPS}
                totalLabel="סה״כ טפסים בטווח שנבחר"
                height={450}
              />
            )
          )}
        </DashboardCard>
      </Box>
    </Box>
  );
};

export default CleanRoomsDashboardPage;
